import { useDebounce } from '@tmap-web-lib/utils/react'
import { RecipeVariants } from '@vanilla-extract/recipes'
import classNames from 'classnames'
import { ButtonHTMLAttributes, forwardRef, MouseEvent } from 'react'

import { buttonRecipe } from './styles.css'

/**
 * 주의사항
 * className에 전달하는 스타일 셀렉터는 클래스 이름만 있어선 안되고 단순 클래스 이름보다 높은 specificity로 만들어야함.
 * 클래스 이름만 있는 셀렉터는 우선순위에 밀려 Button 내부 스타일에 가려짐.
 * - FloatingButton 참고.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  type = 'button',
  variant = 'primary',
  state = 'default',
  size,
  circle,
  iconOnly,
  className,
  children,
  disabled,
  debounceClick,
  onClick,
  ...props
}, ref) {
  const clickDebouncer = useDebounce(typeof debounceClick === 'number' ? { wait: debounceClick } : undefined)
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return
    if (debounceClick) {
      clickDebouncer(() => onClick(event))
    } else {
      onClick(event)
    }
  }

  return (
    <button
      ref={ref}
      data-no-global-style={'touch-target'}
      type={type}
      disabled={disabled}
      className={classNames(
        buttonRecipe({
          variant,
          size,
          state: disabled ? 'disable' : state,
          circle,
          iconOnly,
        }),
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  )
})

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonStyleVariant
  size?: ButtonSizeVariant
  state?: ButtonStateVariant
  iconOnly?: boolean
  circle?: boolean
  debounceClick?: boolean | number
}

type ButtonRecipe = Exclude<RecipeVariants<typeof buttonRecipe>, undefined>
type Variant<Name extends keyof ButtonRecipe> = Exclude<ButtonRecipe[Name], undefined>

export type ButtonStyleVariant = Variant<'variant'>

export type ButtonSizeVariant = Variant<'size'>

export type ButtonStateVariant = Exclude<Variant<'state'>, 'disable'>
